// STALEBUNDLE1 (docs/build-briefs/STALEBUNDLE1-new-version-reload.md): a page left open across a deploy keeps running
// the old bundle. This module lets every page notice that and, at a safe moment, move to the new one.
//
// HOW THE PAGE KNOWS ITS OWN REVISION. ui/build.ts bakes the commit the bundle was built from into the bundle as
// __BUILD_REVISION__ (esbuild define, read from APP_BUILD_REVISION, the same value the server image is built with and
// reports at GET /health as buildRevision). The page then asks GET /health for the server's current revision. A
// difference means a newer deploy exists. A bundle built without the revision ("unknown") never checks, and neither
// does a check whose answer is missing, unreadable or unknown: nothing is shown for a check that cannot be trusted.
//
// COST. One small GET /health (no database, a few dozen bytes) every 5 minutes, plus one when the tab becomes visible
// or the window gains focus, never more than one a minute. A failed check does nothing.
//
// WHAT HAPPENS ON A DIFFERENCE.
//   console: a non-blocking banner below the header, "A new version of Snip & Sip is ready." with a Reload button.
//            The page never reloads itself.
//   floor:   no banner. The page reloads itself, only when isSafeToReload() says nothing could be lost: a safe screen
//            is showing, no sheet, dialog or keypad is open, no field has focus, and no API request is in flight (so
//            a Pay or Close request is never cut off). Otherwise it waits and asks again every few seconds, locally.

declare const __BUILD_REVISION__: string | undefined;

const CHECK_EVERY_MS = 5 * 60 * 1000;
const MIN_GAP_MS = 60 * 1000;
const SAFE_RETRY_MS = 10 * 1000;
const UNKNOWN = "unknown";
const BANNER_ID = "new-version-banner";

export type NewVersionSurface = "console" | "floor";

export interface NewVersionCheckOptions {
  /** The page header. The banner is inserted right after it; the header itself is never touched. */
  headerSelector: string;
  /** Which kind of screen is showing right now. Read at every decision, so a page that holds both can switch. */
  surface: () => NewVersionSurface;
  /** Floor only: true when the visible screen is one where a reload loses nothing (Open Tabs, the user tiles). */
  floorScreenIsSafe?: () => boolean;
}

/** The revision baked into this bundle, or "unknown". */
export function bundledRevision(): string {
  const raw = typeof __BUILD_REVISION__ === "string" ? __BUILD_REVISION__.trim() : "";
  return raw === "" ? UNKNOWN : raw;
}

/** True when the two revisions are both known and differ. Pure, so the decision is easy to read. */
export function revisionsDiffer(loaded: string, current: unknown): boolean {
  if (typeof current !== "string") return false;
  const now = current.trim();
  return loaded !== UNKNOWN && now !== "" && now !== UNKNOWN && now !== loaded;
}

// Requests in flight other than the version check, counted by wrapping fetch once.
let inFlight = 0;
let fetchWrapped = false;
function countRequestsInFlight(): void {
  if (fetchWrapped || typeof window.fetch !== "function") return;
  fetchWrapped = true;
  const original = window.fetch.bind(window);
  window.fetch = (...args: Parameters<typeof fetch>): Promise<Response> => {
    const target = args[0];
    const url =
      typeof target === "string" ? target : target instanceof URL ? target.href : target.url;
    if (/\/health(\?|$)/.test(url)) return original(...args);
    inFlight += 1;
    return original(...args).finally(() => {
      inFlight -= 1;
    });
  };
}

const OVERLAY_SELECTOR = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[aria-modal="true"]',
  "dialog",
  '[class*="scrim"]',
  '[class*="backdrop"]',
].join(",");

function isShown(el: Element): boolean {
  if (el.closest("[hidden]") !== null) return false;
  if (el instanceof HTMLDialogElement && !el.open) return false;
  const style = window.getComputedStyle(el);
  return (
    style.display !== "none" && style.visibility !== "hidden" && el.getClientRects().length > 0
  );
}

/** True when a sheet, dialog or scrim is on screen, or a field or keypad control has focus. */
export function somethingIsOpen(): boolean {
  for (const el of Array.from(document.querySelectorAll(OVERLAY_SELECTOR))) {
    if (el.id === BANNER_ID) continue;
    if (isShown(el)) return true;
  }
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) {
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      active instanceof HTMLSelectElement ||
      active.isContentEditable
    ) {
      return true;
    }
  }
  return false;
}

function placeBanner(headerSelector: string, banner: HTMLElement): void {
  const header = document.querySelector(headerSelector);
  if (header !== null && header.parentElement !== null) header.after(banner);
  else document.body.prepend(banner);
}

function buildBanner(): HTMLElement {
  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.className = "new-version-banner";
  banner.dataset["testid"] = BANNER_ID;
  banner.setAttribute("role", "status");
  banner.hidden = true;
  const text = document.createElement("span");
  text.className = "new-version-banner-text";
  text.textContent = "A new version of Snip & Sip is ready.";
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn-secondary new-version-banner-reload";
  button.dataset["testid"] = "new-version-reload";
  button.textContent = "Reload";
  button.addEventListener("click", () => window.location.reload());
  banner.append(text, button);
  return banner;
}

/** Starts the check for this page. Safe to call once per page load. */
export function startNewVersionCheck(options: NewVersionCheckOptions): void {
  const loaded = bundledRevision();
  if (loaded === UNKNOWN) return;
  if (window.location.protocol === "file:" || window.location.origin === "null") return;
  countRequestsInFlight();

  const banner = buildBanner();
  let stale = false;
  let lastCheck = 0;
  let checking = false;
  let retryTimer: number | undefined;

  const settle = (): void => {
    if (!stale) return;
    // While a newer version waits, look again every few seconds (locally, no request): the person may open a sheet,
    // finish a ticket or move between a console screen and a floor screen.
    if (retryTimer === undefined) {
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        settle();
      }, SAFE_RETRY_MS);
    }
    if (options.surface() === "console") {
      if (banner.parentElement === null) placeBanner(options.headerSelector, banner);
      banner.hidden = false;
      return;
    }
    banner.hidden = true;
    const screenSafe =
      options.floorScreenIsSafe === undefined ? false : options.floorScreenIsSafe();
    if (screenSafe && inFlight === 0 && !somethingIsOpen()) window.location.reload();
  };

  const check = async (): Promise<void> => {
    const now = Date.now();
    if (checking || now - lastCheck < MIN_GAP_MS) return;
    checking = true;
    lastCheck = now;
    try {
      const response = await fetch("/health", { cache: "no-store", credentials: "omit" });
      if (!response.ok) return;
      const body: unknown = await response.json();
      const current =
        typeof body === "object" && body !== null
          ? (body as { buildRevision?: unknown }).buildRevision
          : undefined;
      if (revisionsDiffer(loaded, current)) stale = true;
    } catch {
      // Offline or server down: stay quiet. The next check tries again.
      return;
    } finally {
      checking = false;
    }
    settle();
  };

  window.setInterval(() => void check(), CHECK_EVERY_MS);
  window.addEventListener("focus", () => void check());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void check();
  });
  // A first look shortly after load catches a tab restored from a long sleep.
  window.setTimeout(() => void check(), 5000);
}
